/**
 * A janela de meses do questionário: repetir o mês anterior, trocar o tamanho
 * da janela e decidir o que fazer com mês deixado em branco.
 *
 * Tudo aqui é função pura, e é de propósito: são as três operações em que um
 * erro produz NÚMERO ERRADO em vez de tela quebrada, e número errado não é
 * pego por conferência visual.
 *
 * A REGRA QUE MANDA: mês em branco não pode virar zero medido.
 *
 * `manualEntryTransform` converte campo vazio com `Number(v) || 0`. Com três
 * meses isso era latente — quem entra no questionário preenche os três. Com
 * doze deixa de ser: quem conhece seis meses emitiria seis meses de faturamento
 * zero e o produto diagnosticaria um colapso que ninguém mediu.
 *
 * A resposta é a mesma para qualquer posição: **mês em branco sai da janela**.
 * Nas pontas ou no meio, ele é descartado antes de virar DRE, e o usuário é
 * avisado por nome. Só os meses que alguém realmente mediu chegam ao motor.
 *
 * Até esta onda o mês vazio no MEIO bloqueava o envio. A trava vinha da mesma
 * preocupação certa (lacuna lida como zero), mas cobrava do usuário um dado que
 * ele não tem: quem só conhece março e junho era obrigado a inventar abril e
 * maio ou a encolher a janela. Descartar o mês resolve a preocupação sem a
 * cobrança — o que não foi medido simplesmente não existe na série, e o
 * histórico já sabe exibir competência ausente como travessão
 * (`services/periodoService.projecaoDoDashboard`).
 */

import { camposDeMes } from '../config/planoContas';

/** Um mês está preenchido quando alguma conta de algum grupo tem texto nele. */
const mesPreenchido = (grupos, campo) =>
  Object.values(grupos || {}).some((contas) =>
    (contas || []).some((c) => String(c[campo] ?? '').trim() !== '')
  );

/** Quais meses da janela têm algum valor digitado. */
export const mesesComValor = (formData) => {
  const quantidade = (formData?.meses || []).length;
  return camposDeMes(quantidade).map((campo) => mesPreenchido(formData?.grupos, campo));
};

/**
 * O estado da janela antes de gerar o dashboard.
 *
 * @returns {{vazio, indices, preenchidos, descartados}} — `preenchidos` e
 *   `descartados` são rótulos de mês (os mesmos objetos de `rotulosMeses`),
 *   porque quem consome escreve uma frase com o NOME do mês. `indices` são as
 *   posições preenchidas na janela original, para quem precisar renumerar.
 */
export function situacaoDaJanela(formData) {
  const meses = formData?.meses || [];
  const cheios = mesesComValor(formData);

  const indices = meses.map((_, i) => i).filter((i) => cheios[i]);
  const preenchidos = indices.map((i) => meses[i]);
  const descartados = meses.filter((_, i) => !cheios[i]);

  return { vazio: indices.length === 0, indices, preenchidos, descartados };
}

/**
 * Fica só com os meses preenchidos, em QUALQUER posição, e renumera o que sobrou.
 *
 * Não é só fatiar: os campos são posicionais (`mes1..mesN`), então descartar
 * janeiro obriga a renomear `mes2 -> mes1` em toda conta e a fatiar os rótulos
 * junto. Feito num lugar só porque `StepRevisao` mostra o que vai ser
 * descartado e o `Wizard` aplica na hora de enviar: duas implementações da
 * mesma régua desalinham rótulo de dado — a tela mostra número certo com mês
 * errado, e nada acusa.
 *
 * Os rótulos que sobrevivem carregam o mês REAL de cada valor (`{mes, ano}`), e
 * é isso que torna o descarte do meio seguro: `manualEntryTransform` copia esses
 * rótulos para `dre.periodos`, e `backend/services/lote.competenciasDoDRE` lê
 * dali a competência de cada coluna. Descartar abril não empurra maio para o
 * lugar de abril — maio continua sendo maio na gravação e no gráfico.
 */
export function manterMesesPreenchidos(formData) {
  const situacao = situacaoDaJanela(formData);
  const meses = formData?.meses || [];

  if (situacao.vazio || !situacao.descartados.length) {
    return { formData, descartados: situacao.descartados };
  }

  const antigos = camposDeMes(meses.length);
  const mantidos = situacao.indices.map((i) => antigos[i]);

  const renomear = (conta) => {
    const nova = { ...conta };
    antigos.forEach((campo) => delete nova[campo]);
    mantidos.forEach((campo, i) => {
      nova[`mes${i + 1}`] = conta[campo] ?? '';
    });
    return nova;
  };

  const grupos = Object.fromEntries(
    Object.entries(formData.grupos || {}).map(([id, contas]) => [id, (contas || []).map(renomear)])
  );

  const rotulos = situacao.preenchidos.map((mes, i) => ({ ...mes, campo: `mes${i + 1}` }));

  // A referência acompanha o último mês que sobrou. Sem isto, quem escolhe
  // Abr-Jun e preenche só abril leva um DRE cujo `periodo` diz junho enquanto a
  // única coluna é de abril — e a tela passa a anunciar "Leitura de jun/26"
  // sobre um número de abril.
  const ultimo = rotulos[rotulos.length - 1];
  const config = {
    ...(formData.config || {}),
    mes: ultimo.mes,
    ano: ultimo.ano,
    quantidadeMeses: rotulos.length
  };

  return {
    formData: { ...formData, config, grupos, meses: rotulos },
    descartados: situacao.descartados
  };
}

/**
 * Troca o tamanho da janela sem mudar o mês a que cada valor pertence.
 *
 * A janela termina SEMPRE no mês de referência, então o alinhamento é pela
 * direita: quem sai de 6 para 3 meses fica com os três mais recentes, e quem
 * sobe de 3 para 6 ganha três campos vazios ANTES do que já digitou. Alinhar
 * pela esquerda (o que sai de graça ao só cortar o array) moveria o
 * faturamento de junho para março em silêncio — o pior tipo de erro deste
 * produto, porque os números continuam plausíveis.
 */
export function realinharJanela(grupos, deN, paraN) {
  if (!deN || !paraN || deN === paraN) return grupos;

  const antigos = camposDeMes(deN);
  const novos = camposDeMes(paraN);
  const deslocamento = deN - paraN; // > 0 encolhendo, < 0 crescendo

  const realinhar = (conta) => {
    const nova = { ...conta };
    antigos.forEach((campo) => delete nova[campo]);
    novos.forEach((campo, i) => {
      const origem = antigos[i + deslocamento];
      nova[campo] = origem === undefined ? '' : conta[origem] ?? '';
    });
    return nova;
  };

  return Object.fromEntries(
    Object.entries(grupos || {}).map(([id, contas]) => [id, (contas || []).map(realinhar)])
  );
}

/**
 * Copia os valores de um mês para o outro — SÓ no que está em branco.
 *
 * Preencher por cima do que a pessoa digitou seria afirmar um número que ela
 * não confirmou, que é o defeito que este produto trata como o mais grave. E o
 * atalho só roda por clique: aplicar na montagem da página encheria a tela de
 * medições fabricadas antes de o usuário dizer qualquer coisa.
 *
 * @returns {{grupos, preenchidos, mantidos}} — as contagens viram a frase do
 *   aviso ("12 campos preenchidos com os valores de março"), e é ela que dá ao
 *   usuário como saber o que aconteceu antes de desfazer.
 */
export function repetirMes(grupos, { de, para, apenasGrupo = null }) {
  let preenchidos = 0;
  let mantidos = 0;

  const copiar = (conta) => {
    const origem = String(conta[de] ?? '').trim();
    const destino = String(conta[para] ?? '').trim();
    if (origem === '') return conta;
    if (destino !== '') {
      mantidos += 1;
      return conta;
    }
    preenchidos += 1;
    return { ...conta, [para]: conta[de] };
  };

  const novos = Object.fromEntries(
    Object.entries(grupos || {}).map(([id, contas]) => [
      id,
      apenasGrupo && id !== apenasGrupo ? contas : (contas || []).map(copiar)
    ])
  );

  return { grupos: novos, preenchidos, mantidos };
}
